import type { LegalDocumentBody } from '../legal/legalDocuments';

const URL_RE = /(https?:\/\/[^\s]+)/g;

/** Stable anchors for footer deep-links (Security, cookies, DPDP rights). */
export function legalSectionId(heading: string): string | undefined {
    const h = heading.toLowerCase();
    if (h.includes('cookies')) return 'cookies';
    if (h.includes('protection of information')) return 'protection-of-information';
    if (h.includes('control') && h.includes('amendment')) return 'privacy-rights';
    if (h.includes('individual privacy rights')) return 'privacy-contact';
    return undefined;
}


function LinkedText({ text, className }: { text: string; className: string }) {
    const parts = text.split(URL_RE);
    return (
        <p className={className}>
            {parts.map((part, i) => {
                if (!/^https?:\/\//.test(part)) {
                    return <span key={i}>{part}</span>;
                }
                const trailing = part.match(/[.,;:]+$/)?.[0] ?? '';
                const href = trailing ? part.slice(0, -trailing.length) : part;
                return (
                    <span key={`${href}-${i}`}>
                        <a href={href} className="text-primary underline break-all" target="_blank" rel="noreferrer">
                            {href}
                        </a>
                        {trailing}
                    </span>
                );
            })}
        </p>
    );
}

export function LegalDocumentText({
    body,
    compact = false,
}: {
    body: LegalDocumentBody;
    compact?: boolean;
}) {
    return (
        <article className={compact ? 'space-y-3' : 'mx-auto max-w-3xl px-4 py-10'}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
                CryptoFlow · Outris · Version {body.version}
            </p>
            {compact ? (
                <h3 className="text-sm font-semibold text-foreground">{body.title}</h3>
            ) : (
                <h1 className="mt-2 text-3xl font-bold text-foreground">{body.title}</h1>
            )}
            <p className={compact ? 'text-xs text-muted-foreground' : 'mt-1 text-sm text-muted-foreground'}>
                Effective {body.effectiveDate}
            </p>
            {!body.counselSigned && (
                <p
                    className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100"
                    data-testid="legal-product-notice"
                >
                    Product notice — not a statutory privacy notice. Agency counsel has not signed this text.
                </p>
            )}
            <LinkedText
                text={body.intro}
                className={
                    compact
                        ? 'text-xs leading-relaxed text-foreground/90'
                        : 'mt-6 text-sm leading-relaxed text-foreground/90'
                }
            />
            {body.sections.map((section) => (
                <section
                    key={section.heading}
                    id={legalSectionId(section.heading)}
                    className={compact ? 'space-y-2' : 'mt-8 scroll-mt-24'}
                >
                    <h2
                        className={
                            compact
                                ? 'text-xs font-semibold text-foreground'
                                : 'text-lg font-semibold text-foreground'
                        }
                    >
                        {section.heading}
                    </h2>
                    {section.paragraphs.map((p, i) => (
                        <LinkedText
                            key={`${section.heading}-p-${i}`}
                            text={p}
                            className={
                                compact
                                    ? 'text-xs leading-relaxed text-foreground/85'
                                    : 'mt-3 text-sm leading-relaxed text-foreground/85'
                            }
                        />
                    ))}
                    {section.bullets && (
                        <ul
                            className={
                                compact
                                    ? 'list-disc space-y-1 pl-4 text-xs text-foreground/85'
                                    : 'mt-3 list-disc space-y-1 pl-5 text-sm text-foreground/85'
                            }
                        >
                            {section.bullets.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    )}
                </section>
            ))}
        </article>
    );
}
